import { useEffect, useState } from "react";

/**
 * Quill — Oak AI Campus's mascot.
 *
 * Lore: oak galls were historically used to brew the world's most enduring
 * ink — the Magna Carta, the US Constitution, and Shakespeare's manuscripts
 * were all penned with oak-gall ink. Quill is a sentient acorn-sprite who
 * spots trending knowledge and brings it to learners. Hence the name.
 *
 * Visual: a chubby acorn-shaped body in OAK-brand mint/cobalt with big
 * curious eyes and a leaf cap. Idle breathing + occasional blink animation
 * make it feel alive without being distracting.
 *
 * Usage:
 *   <QuillMascot size={48} />
 *   <QuillMascot size="hero" speech="Spotted 12 learners on this." />
 */

const SIZES = {
  xs: 24,
  sm: 36,
  md: 56,
  lg: 80,
  hero: 110,
};

export default function QuillMascot({ size = "md", speech, className = "", testid = "quill-mascot" }) {
  const px = typeof size === "number" ? size : (SIZES[size] || SIZES.md);
  const [blink, setBlink] = useState(false);

  // Random blink cadence — feels organic, not metronome-y
  useEffect(() => {
    let timer;
    const schedule = () => {
      const next = 2400 + Math.random() * 3200; // 2.4–5.6s
      timer = setTimeout(() => {
        setBlink(true);
        setTimeout(() => setBlink(false), 140);
        schedule();
      }, next);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`quill-wrap inline-flex items-center gap-3 ${className}`}
      style={{ "--quill-px": `${px}px` }}
      data-testid={testid}
    >
      <svg
        width={px}
        height={px}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        className="quill-svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={`quill-cap-${px}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#00FF9D" />
            <stop offset="1" stopColor="#00B073" />
          </linearGradient>
          <linearGradient id={`quill-body-${px}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3D6BFF" />
            <stop offset="1" stopColor="#0044FF" />
          </linearGradient>
          <radialGradient id={`quill-cheek-${px}`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="rgba(0,255,157,0.45)" />
            <stop offset="1" stopColor="rgba(0,255,157,0)" />
          </radialGradient>
          <filter id={`quill-glow-${px}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Soft ambient glow under Quill */}
        <ellipse cx="60" cy="106" rx="22" ry="4" fill="rgba(0,68,255,0.18)" />

        {/* Stem sprout — curls slightly */}
        <path
          d="M60 18 Q 64 8 72 10"
          stroke="#7DDDB2"
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="72" cy="10" r="2.2" fill="#7DDDB2" />

        {/* Oak leaf cap — slightly tilted, gives Quill personality */}
        <g transform="translate(60 38) rotate(-8)" filter={`url(#quill-glow-${px})`}>
          <path
            d="M 0 -20
               C -6 -22, -16 -14, -22 -6
               C -28 0, -24 8, -16 8
               C -8 8, -4 4, 0 6
               C 4 4, 8 8, 16 8
               C 24 8, 28 0, 22 -6
               C 16 -14, 6 -22, 0 -20 Z"
            fill={`url(#quill-cap-${px})`}
            stroke="#003922"
            strokeWidth="0.8"
            strokeOpacity="0.4"
          />
          {/* leaf veins */}
          <path d="M0 -18 L0 8" stroke="#003922" strokeWidth="0.8" strokeOpacity="0.35" />
          <path d="M-12 -2 Q -6 -6 0 -4" stroke="#003922" strokeWidth="0.6" strokeOpacity="0.25" fill="none" />
          <path d="M12 -2 Q 6 -6 0 -4" stroke="#003922" strokeWidth="0.6" strokeOpacity="0.25" fill="none" />
        </g>

        {/* Acorn body */}
        <g className="quill-body">
          {/* main acorn shape */}
          <path
            d="M 30 60
               Q 30 40 60 40
               Q 90 40 90 60
               L 88 78
               Q 84 100 60 100
               Q 36 100 32 78
               Z"
            fill={`url(#quill-body-${px})`}
            stroke="#001A66"
            strokeWidth="1"
            strokeOpacity="0.5"
          />

          {/* Cheek glow */}
          <ellipse cx="44" cy="78" rx="8" ry="5" fill={`url(#quill-cheek-${px})`} opacity="0.7" />
          <ellipse cx="76" cy="78" rx="8" ry="5" fill={`url(#quill-cheek-${px})`} opacity="0.7" />

          {/* Eyes */}
          <g className={`quill-eyes ${blink ? "quill-eyes--blink" : ""}`}>
            {/* whites */}
            <ellipse cx="48" cy="66" rx="7" ry="8" fill="#FFFFFF" />
            <ellipse cx="72" cy="66" rx="7" ry="8" fill="#FFFFFF" />
            {/* pupils */}
            <circle cx="49" cy="68" r="3.4" fill="#001A4D" />
            <circle cx="73" cy="68" r="3.4" fill="#001A4D" />
            {/* sparkle */}
            <circle cx="50.4" cy="66.5" r="1.2" fill="#FFFFFF" />
            <circle cx="74.4" cy="66.5" r="1.2" fill="#FFFFFF" />
          </g>

          {/* Smile */}
          <path
            d="M 53 84 Q 60 90 67 84"
            stroke="#FFFFFF"
            strokeWidth="2.2"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      </svg>

      {speech && (
        <div className="quill-speech" data-testid="quill-speech">
          <span className="quill-speech-name">Quill</span>
          <span className="quill-speech-body">{speech}</span>
        </div>
      )}
    </div>
  );
}
